"use client";

import * as React from "react";
import {
  useIntl,
  type MessageDescriptor,
  type MessageValues,
} from "./intl-context.js";

type TranslationProps = MessageDescriptor & {
  trailingSpace?: boolean;
  values?: MessageValues;
};

/**
 * Translation component that gracefully degrades when no I18nProvider is present.
 *
 * - With I18nProvider: Uses react-intl for full i18n support (react-intl will be bundled)
 * - Without I18nProvider: Renders the defaultMessage with simple value interpolation (no react-intl bundled)
 */
export const Translation: React.FC<TranslationProps> = (props) => {
  const { defaultMessage, id, description, trailingSpace, values } = props;
  const i18n = useIntl();

  // If I18nProvider is present, use react-intl's formatting
  if (i18n) {
    const str = i18n.formatMessage({ id, defaultMessage, description }, values);
    return trailingSpace ? <>{str} </> : str;
  }

  // If no I18nProvider is present, render the defaultMessage with simple interpolation
  if (!values) {
    return trailingSpace ? <>{defaultMessage} </> : defaultMessage;
  }

  // Simple interpolation: split by placeholders and interleave with values
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = /\{(\w+)\}/g;
  let match;

  while ((match = regex.exec(defaultMessage)) !== null) {
    const [placeholder, key] = match;

    // Add text before the placeholder
    if (match.index > lastIndex) {
      parts.push(defaultMessage.substring(lastIndex, match.index));
    }

    // Add the interpolated value or placeholder if not found
    parts.push(values[key] ?? placeholder);
    lastIndex = match.index + placeholder.length;
  }

  // Add remaining text
  if (lastIndex < defaultMessage.length) {
    parts.push(defaultMessage.substring(lastIndex));
  }

  return trailingSpace ? <>{parts} </> : parts;
};
